import { Link, Navigate, useLocation, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { CourseAnnouncementsList } from "@/components/course-announcements-list";
import { CourseNavLinks } from "@/components/course-nav-links";
import { useAuthStore } from "@/stores/authStore";
import { fetchPlayerOutline } from "@/services/api/player";

/** COMM-3: `/t/:slug/learn/:courseSlug/announcements` — a course's announcements in full. */
export function TenantLearnAnnouncementsPage() {
  const { tenantSlug = "", courseSlug = "" } = useParams<{
    tenantSlug: string;
    courseSlug: string;
  }>();
  const tNav = useTranslations("nav");
  const t = useTranslations("announcements");
  const tDetail = useTranslations("courseDetail");
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  // I-5: a notification links here with `#a-<announcementId>`.
  const { hash } = useLocation();
  const targetId = hash.startsWith("#a-") ? decodeURIComponent(hash.slice(3)) : null;

  const outlineQuery = useQuery({
    queryKey: ["player-outline", courseSlug],
    queryFn: () => fetchPlayerOutline(courseSlug),
    enabled: isAuthenticated && Boolean(courseSlug),
    retry: false,
  });

  if (!isAuthenticated) {
    return <Navigate to={`/t/${tenantSlug}/login`} replace />;
  }

  if (outlineQuery.isLoading) {
    return (
      <main className="mx-auto max-w-3xl px-4 py-6 sm:px-6 lg:py-8">
        <p className="text-sm text-[var(--text-muted)]">{t("loading")}</p>
      </main>
    );
  }

  if (outlineQuery.isError || !outlineQuery.data) {
    const status = (outlineQuery.error as { response?: { status?: number } } | null)?.response
      ?.status;
    if (status === 403) {
      return <Navigate to={`/t/${tenantSlug}/courses/${courseSlug}`} replace />;
    }
    return (
      <main
        className="mx-auto max-w-3xl px-4 py-6 sm:px-6 lg:py-8"
        data-testid="course-announcements-not-found"
      >
        <p className="text-sm text-[var(--text-muted)]">{tDetail("notFound")}</p>
      </main>
    );
  }

  const { course } = outlineQuery.data;
  const firstLesson = outlineQuery.data.modules.flatMap((mod) => mod.lessons)[0];

  return (
    <main
      className="mx-auto max-w-3xl px-4 py-6 sm:px-6 lg:py-8"
      data-testid="course-announcements-page"
    >
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
        <Link
          to={`/t/${tenantSlug}/dashboard`}
          className="text-[var(--text-muted)] hover:underline"
        >
          {tNav("backToDashboard")}
        </Link>
        {firstLesson ? (
          <Link
            to={`/t/${tenantSlug}/learn/${courseSlug}`}
            className="text-[var(--text-heading)] hover:underline"
          >
            {course.title}
          </Link>
        ) : null}
      </div>

      <header className="mt-4">
        <p className="text-sm text-[var(--text-muted)]">{course.title}</p>
        <h1 className="font-serif text-2xl font-semibold text-[var(--brand-heading)]">
          {t("title")}
        </h1>
        <div className="mt-2">
          <CourseNavLinks tenantSlug={tenantSlug} courseSlug={courseSlug} active="announcements" />
        </div>
      </header>

      <div className="mt-6">
        <CourseAnnouncementsList courseId={course.id} targetId={targetId} />
      </div>
    </main>
  );
}
